import { Button, DatePicker, Drawer, Form, Input, InputNumber, Table } from "antd";
import Select from "../../../shared/components/searchable-select.component";
import type { ColumnsType } from "antd/es/table";
import moment, { Moment } from "moment";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  getStaffLopList,
  upsertStaffLop,
} from "../../../core/store/reducers/staff.slice";
import { showNotification } from "../../../shared/utils/utils";

interface Props {
  onClose: () => void;
  visible: boolean;
  selectedEntity?: any;
  onSubmit?: () => void;
}

type LopRow = {
  id?: string;
  month: string;
  lopPercent: number;
  category?: string;
  remarks?: string;
};

const LOP_CATEGORY_OPTIONS = [
  "Training",
  "Short Billing",
  "Maternity",
  "No Work",
  "Exit Bench",
].map((value) => ({ label: value, value }));

const StaffLopComponent = ({
  onClose,
  visible,
  selectedEntity,
  onSubmit = () => {
    return;
  },
}: Props) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [rows, setRows] = useState<LopRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const staffId = selectedEntity?.staffId;

  const fetchRows = useCallback(async () => {
    if (!staffId) return;
    setLoading(true);
    try {
      const response = await dispatch(getStaffLopList({ staffId })).unwrap();
      setRows(response?.data?.data || []);
    } catch {
      showNotification("Failed to load LOP", "error");
    } finally {
      setLoading(false);
    }
  }, [dispatch, staffId]);

  useEffect(() => {
    if (visible && staffId) {
      form.resetFields();
      fetchRows();
    }
  }, [visible, staffId, form, fetchRows]);

  const onFinish = async (values: {
    month: Moment;
    lopPercent: number;
    category: string;
    remarks?: string;
  }) => {
    if (!staffId) return;
    setSaving(true);
    try {
      const response = await dispatch(
        upsertStaffLop({
          staffId,
          allocation: [
            {
              month: values.month.format("YYYY-MM"),
              lopPercent: Number(values.lopPercent) || 0,
              category: values.category,
              remarks: values.remarks || "",
            },
          ],
        })
      ).unwrap();
      if (response?.status >= 400 || response?.data?.statusCode === 400) {
        showNotification(
          response?.data?.message || "Failed to update LOP",
          "error",
        );
        return;
      }
      showNotification("LOP updated successfully", "success");
      form.resetFields();
      await fetchRows();
      onSubmit();
    } catch (error: any) {
      showNotification(
        error?.response?.data?.message || "Failed to update LOP",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<LopRow> = [
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      render: (value: string) => moment(value, "YYYY-MM").format("MMM YYYY"),
    },
    {
      title: "LOP %",
      dataIndex: "lopPercent",
      key: "lopPercent",
      align: "right",
      render: (value: number) => `${Number(value || 0)}%`,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      render: (value?: string) => value || "-",
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      render: (value?: string) => value || "-",
    },
  ];

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      title={`LOP - ${selectedEntity?.staffName || "Staff"}`}
      width={560}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ month: moment(), lopPercent: 0 }}
      >
        <Form.Item
          name="month"
          label="Month"
          rules={[{ required: true, message: "Please select month" }]}
        >
          <DatePicker picker="month" format="MMM YYYY" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="lopPercent"
          label="LOP %"
          rules={[{ required: true, message: "Please enter LOP %" }]}
        >
          <InputNumber min={0} max={100} addonAfter="%" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="category"
          label="Category"
          rules={[{ required: true, message: "Please select category" }]}
        >
          <Select options={LOP_CATEGORY_OPTIONS} placeholder="Select category" />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={3} />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={saving}>
          Save
        </Button>
      </Form>

      <Table
        className="mt-4"
        rowKey={(row) => row.id || row.month}
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        size="small"
      />
    </Drawer>
  );
};

export default StaffLopComponent;
